import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, Pencil, Trash2, Target, Users, UserPlus, Calendar,
  ChevronRight, ChevronLeft, AlertTriangle, CheckCircle, 
  ClipboardList, RefreshCw, Search, Filter, FileEdit
} from "lucide-react";

export default function EmployeeTargets({ user }) {
  const [activeTab, setActiveTab] = useState("assignments");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [assignments, setAssignments] = useState([]);
  const [targets, setTargets] = useState([]);
  const [kraTypes, setKraTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filter states
  const [filterDepartment, setFilterDepartment] = useState("all");
  const [filterEmployeeType, setFilterEmployeeType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Period states
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  // Selected assignment for target management
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Assignment Dialog
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({
    employee_id: "",
    employee_type: "employee",
    kra_type_id: "",
    department_id: "",
    approver_type: "manager",
    approver_id: "",
    effective_from: new Date().toISOString().split('T')[0]
  });
  
  // Change Assignment Dialog (for changing KRA type of existing assignment)
  const [changeAssignmentDialog, setChangeAssignmentDialog] = useState({ open: false, assignment: null });
  const [changeAssignmentForm, setChangeAssignmentForm] = useState({
    new_kra_type_id: "",
    new_effective_from: new Date().toISOString().split('T')[0],
    new_approver_type: "manager",
    new_approver_id: "",
    change_reason: ""
  });
  
  // Target Dialog
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetForm, setTargetForm] = useState({
    targets: []  // Array of {kra_line_item_id, target_value, responsible_user_id}
  });
  
  // Performance Actuals (Phase 3)
  const [targetsWithActuals, setTargetsWithActuals] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [manualActualDialog, setManualActualDialog] = useState({ open: false, target: null });
  const [manualActualForm, setManualActualForm] = useState({ actual_value: "", notes: "" });
  
  // Revise Dialog
  const [reviseDialogOpen, setReviseDialogOpen] = useState(false);
  const [reviseTarget, setReviseTarget] = useState(null);
  const [reviseForm, setReviseForm] = useState({
    new_target_value: "",
    effective_from: new Date().toISOString().split('T')[0],
    revision_reason: ""
  });
  
  // Edit Target Dialog (direct edit without revision - for Admin/Management)
  const [editTargetDialogOpen, setEditTargetDialogOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [editTargetForm, setEditTargetForm] = useState({
    target_value: ""
  });
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null });

  // Permissions
  const permissions = user?.permissions?.employeeTargets || {};
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;
  
  // Check if user is Admin/Management (can directly edit targets without revision)
  const isAdminOrManagement = user?.department === 'Management' || 
                              user?.access_level === 'Admin' || 
                              user?.role?.includes('ADMIN') ||
                              user?.role?.includes('MANAGEMENT');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedAssignment) {
      fetchTargets(selectedAssignment.id);
    }
  }, [selectedAssignment, selectedYear, selectedMonth]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, kraTypesRes, employeesRes, approversRes, deptRes] = await Promise.all([
        api.get("/employee-kra-assignments"),
        api.get("/kra-types"),
        api.get("/employees-for-kra"),
        api.get("/users-for-approver"),
        api.get("/departments-list")
      ]);
      setAssignments(assignmentsRes.data);
      setKraTypes(kraTypesRes.data.filter(k => k.is_active));
      setEmployees(employeesRes.data);
      setApprovers(approversRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTargets = async (assignmentId) => {
    try {
      const res = await api.get(`/employee-targets?assignment_id=${assignmentId}&period_year=${selectedYear}&period_month=${selectedMonth}`);
      setTargets(res.data);
      
      // Also fetch targets with actuals for the performance tracking view
      const actualsRes = await api.get(`/performance/targets-with-actuals?assignment_id=${assignmentId}&period_year=${selectedYear}&period_month=${selectedMonth}`);
      setTargetsWithActuals(actualsRes.data);
    } catch (error) {
      toast.error("Failed to load targets");
    }
  };

  const fetchLineItemsForKraType = async (kraTypeId) => {
    try {
      const res = await api.get(`/kra-line-items?kra_type_id=${kraTypeId}`);
      setLineItems(res.data.filter(li => li.is_active));
    } catch (error) {
      toast.error("Failed to load line items");
    }
  };

  // Assignment Handlers
  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAssignment) {
        await api.put(`/employee-kra-assignments/${editingAssignment.id}`, {
          approver_type: assignmentForm.approver_type,
          approver_id: assignmentForm.approver_id
        });
        toast.success("Assignment updated successfully");
      } else {
        await api.post("/employee-kra-assignments", assignmentForm);
        toast.success("Assignment created successfully");
      }
      setAssignmentDialogOpen(false);
      resetAssignmentForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save assignment");
    }
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setAssignmentForm({
      employee_id: assignment.employee_id,
      employee_type: assignment.employee_type,
      kra_type_id: assignment.kra_type_id,
      department_id: assignment.department_id,
      approver_type: assignment.approver_type,
      approver_id: assignment.approver_id,
      effective_from: assignment.effective_from
    });
    setAssignmentDialogOpen(true);
  };

  const handleDeleteAssignment = async () => {
    try {
      await api.delete(`/employee-kra-assignments/${deleteDialog.item.id}`);
      toast.success("Assignment deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchData();
      if (selectedAssignment?.id === deleteDialog.item.id) {
        setSelectedAssignment(null);
        setTargets([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete assignment");
    }
  };

  const resetAssignmentForm = () => {
    setEditingAssignment(null);
    setAssignmentForm({
      employee_id: "",
      employee_type: "employee",
      kra_type_id: "",
      department_id: "",
      approver_type: "manager",
      approver_id: "",
      effective_from: new Date().toISOString().split('T')[0]
    });
  };

  // Change Assignment Handlers (for changing KRA type)
  const openChangeAssignmentDialog = (assignment) => {
    setChangeAssignmentDialog({ open: true, assignment });
    setChangeAssignmentForm({
      new_kra_type_id: "",
      new_effective_from: new Date().toISOString().split('T')[0],
      new_approver_type: assignment.approver_type || "manager",
      new_approver_id: assignment.approver_id || "",
      change_reason: ""
    });
  };

  const handleChangeAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/employee-kra-assignments/${changeAssignmentDialog.assignment.id}/change`, {
        new_kra_type_id: changeAssignmentForm.new_kra_type_id,
        new_effective_from: changeAssignmentForm.new_effective_from,
        new_approver_type: changeAssignmentForm.new_approver_type,
        new_approver_id: changeAssignmentForm.new_approver_id,
        change_reason: changeAssignmentForm.change_reason
      });
      toast.success("KRA assignment changed successfully");
      setChangeAssignmentDialog({ open: false, assignment: null });
      fetchData();
      setSelectedAssignment(null);
      setTargets([]);
      setTargetsWithActuals([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change assignment");
    }
  };

  // Target Handlers
  const openTargetDialog = async () => {
    if (!selectedAssignment) return;
    
    // Fetch line items for the KRA type
    await fetchLineItemsForKraType(selectedAssignment.kra_type_id);
    
    // Initialize target form with line items
    setTargetForm({
      targets: lineItems.map(li => ({
        kra_line_item_id: li.id,
        kra_line_item_name: li.name,
        weightage: li.weightage,
        measurement_unit: li.measurement_unit,
        target_value: "",
        data_source_type: li.data_source?.type || "manual",
        responsible_user_id: ""
      }))
    });
    setTargetDialogOpen(true);
  };

  useEffect(() => {
    if (lineItems.length > 0 && targetDialogOpen) {
      setTargetForm({
        targets: lineItems.map(li => ({
          kra_line_item_id: li.id,
          kra_line_item_name: li.name,
          weightage: li.weightage,
          measurement_unit: li.measurement_unit,
          target_value: "",
          data_source_type: li.data_source?.type || "manual",
          responsible_user_id: ""
        }))
      });
    }
  }, [lineItems, targetDialogOpen]);

  const handleTargetSubmit = async (e) => {
    e.preventDefault();
    try {
      const validTargets = targetForm.targets.filter(t => t.target_value !== "" && parseFloat(t.target_value) >= 0);
      
      if (validTargets.length === 0) {
        toast.error("Please enter at least one target value");
        return;
      }

      await api.post("/employee-targets/bulk-create", {
        assignment_id: selectedAssignment.id,
        employee_id: selectedAssignment.employee_id,
        period_year: selectedYear,
        period_month: selectedMonth,
        effective_from: `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-01`,
        targets: validTargets.map(t => ({
          kra_line_item_id: t.kra_line_item_id,
          target_value: parseFloat(t.target_value),
          responsible_user_id: t.responsible_user_id || null
        }))
      });
      
      toast.success("Targets created successfully");
      setTargetDialogOpen(false);
      fetchTargets(selectedAssignment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save targets");
    }
  };

  const updateTargetValue = (index, field, value) => {
    const newTargets = [...targetForm.targets];
    newTargets[index] = { ...newTargets[index], [field]: value };
    setTargetForm({ ...targetForm, targets: newTargets });
  };

  // Revise Target Handlers
  const openReviseDialog = (target) => {
    setReviseTarget(target);
    setReviseForm({
      new_target_value: target.target_value.toString(),
      effective_from: new Date().toISOString().split('T')[0],
      revision_reason: ""
    });
    setReviseDialogOpen(true);
  };

  const handleReviseSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/employee-targets/${reviseTarget.id}/revise`, {
        new_target_value: parseFloat(reviseForm.new_target_value),
        effective_from: reviseForm.effective_from,
        revision_reason: reviseForm.revision_reason
      });
      toast.success("Target revised successfully");
      setReviseDialogOpen(false);
      fetchTargets(selectedAssignment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revise target");
    }
  };

  // Direct Edit Target Handlers (for Admin/Management - without revision)
  const openEditTargetDialog = (target) => {
    setEditingTarget(target);
    setEditTargetForm({
      target_value: target.target_value.toString()
    });
    setEditTargetDialogOpen(true);
  };

  const handleEditTargetSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/employee-targets/${editingTarget.id}`, {
        target_value: parseFloat(editTargetForm.target_value)
      });
      toast.success("Target updated successfully");
      setEditTargetDialogOpen(false);
      setEditingTarget(null);
      fetchTargets(selectedAssignment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update target");
    }
  };

  const handleDeleteTarget = async () => {
    try {
      await api.delete(`/employee-targets/${deleteDialog.item.id}`);
      toast.success("Target deleted successfully");
      setDeleteDialog({ open: false, type: null, item: null });
      fetchTargets(selectedAssignment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete target");
    }
  };

  // Phase 3: Sync and Manual Actual Handlers
  const handleSyncActuals = async () => {
    if (!selectedAssignment) return;
    setSyncing(true);
    try {
      const res = await api.post('/performance/actuals/sync-all', {
        employee_id: selectedAssignment.employee_id,
        period_year: selectedYear,
        period_month: selectedMonth
      });
      toast.success(`Synced ${res.data.total_synced} actuals (Timesheets: ${res.data.timesheets_synced}, Errors: ${res.data.errors_synced})`);
      fetchTargets(selectedAssignment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync actuals");
    } finally {
      setSyncing(false);
    }
  };

  const openManualActualDialog = (target) => {
    setManualActualDialog({ open: true, target });
    setManualActualForm({
      actual_value: target.actual_value?.toString() || "",
      notes: target.actual_notes || ""
    });
  };

  const handleManualActualSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/performance/actuals/manual', {
        target_id: manualActualDialog.target.id,
        actual_value: parseFloat(manualActualForm.actual_value),
        notes: manualActualForm.notes
      });
      toast.success("Actual value saved successfully");
      setManualActualDialog({ open: false, target: null });
      fetchTargets(selectedAssignment.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save actual value");
    }
  };

  // Filter assignments
  const filteredAssignments = assignments.filter(a => {
    if (filterDepartment !== "all" && a.department_id !== filterDepartment) return false;
    if (filterEmployeeType !== "all" && a.employee_type !== filterEmployeeType) return false;
    if (searchTerm && !a.employee_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-md3-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-headline-medium text-md3-on-surface font-medium">Employee Targets</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant mt-1">
              Assign KRAs to employees and set monthly targets
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-md3-surface-container rounded-md3-full p-1">
            <TabsTrigger 
              value="assignments" 
              className="rounded-md3-full data-[state=active]:bg-md3-primary data-[state=active]:text-md3-on-primary"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              KRA Assignments
            </TabsTrigger>
            <TabsTrigger 
              value="targets" 
              className="rounded-md3-full data-[state=active]:bg-md3-primary data-[state=active]:text-md3-on-primary"
            >
              <Target className="h-4 w-4 mr-2" />
              Monthly Targets
            </TabsTrigger>
          </TabsList>

          {/* KRA Assignments Tab */}
          <TabsContent value="assignments" className="space-y-4 mt-6">
            {/* Filters */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-md3-on-surface-variant" />
                <Input
                  placeholder="Search employee..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-md3-surface-container border-md3-outline rounded-md3-full"
                />
              </div>
              <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                <SelectTrigger className="w-48 bg-md3-surface-container border-md3-outline rounded-md3-sm">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterEmployeeType} onValueChange={setFilterEmployeeType}>
                <SelectTrigger className="w-40 bg-md3-surface-container border-md3-outline rounded-md3-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="employee">Employees</SelectItem>
                  <SelectItem value="intern">Interns</SelectItem>
                </SelectContent>
              </Select>
              {canCreate && (
                <Button 
                  onClick={() => { resetAssignmentForm(); setAssignmentDialogOpen(true); }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full ml-auto"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Assignment
                </Button>
              )}
            </div>

            {/* Assignments Table */}
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="p-0">
                {filteredAssignments.length === 0 ? (
                  <div className="text-center py-12 text-md3-body-medium text-md3-on-surface-variant">
                    <Users className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                    <p>No KRA assignments found.</p>
                    <p className="text-md3-body-small mt-1">Create assignments to start tracking employee performance.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-md3-outline-variant hover:bg-transparent">
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Employee</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Type</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">KRA Type</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Department</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Approver</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Effective From</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant">Status</TableHead>
                        <TableHead className="text-md3-label-large text-md3-on-surface-variant text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAssignments.map((assignment) => (
                        <TableRow 
                          key={assignment.id} 
                          className={`border-md3-outline-variant cursor-pointer hover:bg-md3-surface-container ${
                            selectedAssignment?.id === assignment.id ? 'bg-md3-secondary-container' : ''
                          }`}
                          onClick={() => { setSelectedAssignment(assignment); setActiveTab("targets"); }}
                        >
                          <TableCell className="text-md3-body-medium text-md3-on-surface font-medium">
                            {assignment.employee_name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              assignment.employee_type === 'intern' 
                                ? 'border-md3-tertiary text-md3-tertiary' 
                                : 'border-md3-primary text-md3-primary'
                            }>
                              {assignment.employee_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-md3-body-medium text-md3-on-surface">
                            {assignment.kra_type_name}
                          </TableCell>
                          <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                            {assignment.kra_department_name}
                          </TableCell>
                          <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                            <div className="flex flex-col">
                              <span>{assignment.approver_name}</span>
                              <span className="text-xs text-md3-on-surface-variant">({assignment.approver_type})</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-md3-body-medium text-md3-on-surface-variant">
                            {assignment.effective_from}
                            {assignment.effective_to && (
                              <span className="text-xs block">to {assignment.effective_to}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              assignment.status === 'current' ? 'bg-md3-tertiary-container text-md3-on-tertiary-container' : 
                              assignment.status === 'historical' ? 'bg-md3-surface-container-highest text-md3-on-surface-variant' :
                              'bg-md3-primary-container text-md3-on-primary-container'
                            }>
                              {assignment.status === 'current' ? 'Current' : 
                               assignment.status === 'historical' ? 'Historical' : 'Future'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              {canEdit && assignment.status === 'current' && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => openChangeAssignmentDialog(assignment)}
                                  className="h-8 w-8 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                  title="Change KRA Type"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                              )}
                              {canDelete && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => setDeleteDialog({ open: true, type: 'assignment', item: assignment })}
                                  className="h-8 w-8 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                              <ChevronRight className="h-4 w-4 text-md3-on-surface-variant" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Targets Tab */}
          <TabsContent value="targets" className="space-y-4 mt-6">
            {!selectedAssignment ? (
              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <ClipboardList className="h-16 w-16 text-md3-outline mb-4" />
                  <h3 className="text-md3-title-large text-md3-on-surface mb-2">Select an Assignment</h3>
                  <p className="text-md3-body-medium text-md3-on-surface-variant text-center max-w-md">
                    Go to &quot;KRA Assignments&quot; tab and select an employee to manage their monthly targets.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setActiveTab("assignments")}
                    className="mt-4 border-md3-outline text-md3-primary rounded-md3-full"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Go to Assignments
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Selected Employee Info */}
                <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                          <Users className="h-6 w-6 text-md3-on-primary-container" />
                        </div>
                        <div>
                          <h3 className="text-md3-title-medium text-md3-on-surface">{selectedAssignment.employee_name}</h3>
                          <p className="text-md3-body-small text-md3-on-surface-variant">
                            {selectedAssignment.kra_type_name} • {selectedAssignment.kra_department_name}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => setSelectedAssignment(null)}
                        className="border-md3-outline text-md3-primary rounded-md3-full"
                      >
                        Change Employee
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Period Selector */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-md3-primary" />
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="w-40 bg-md3-surface-container border-md3-outline rounded-md3-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(m => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-28 bg-md3-surface-container border-md3-outline rounded-md3-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026].map(y => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {canCreate && targets.length === 0 && (
                    <Button 
                      onClick={openTargetDialog}
                      className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Set Targets
                    </Button>
                  )}
                </div>

                {/* Targets & Performance Table */}
                <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-md3-title-large text-md3-on-surface">
                          {months.find(m => m.value === selectedMonth)?.label} {selectedYear} Performance
                        </CardTitle>
                        <CardDescription className="text-md3-body-small text-md3-on-surface-variant">
                          Targets, actuals, and achievement tracking
                          {targetsWithActuals.length > 0 && targetsWithActuals[0]?.is_carried_forward && (
                            <Badge variant="outline" className="ml-2 border-md3-tertiary text-md3-tertiary">
                              Targets carried forward from {months.find(m => m.value === targetsWithActuals[0]?.carried_from_month)?.label} {targetsWithActuals[0]?.carried_from_year}
                            </Badge>
                          )}
                        </CardDescription>
                      </div>
                      {targetsWithActuals.length > 0 && (
                        <Button
                          onClick={handleSyncActuals}
                          disabled={syncing}
                          variant="outline"
                          size="sm"
                          className="border-md3-tertiary text-md3-tertiary hover:bg-md3-tertiary-container rounded-md3-full"
                        >
                          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                          {syncing ? 'Syncing...' : 'Sync Actuals'}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {targetsWithActuals.length === 0 ? (
                      <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                        <Target className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                        <p>No targets set for this period.</p>
                        <p className="text-md3-body-small mt-1">Click &quot;Set Targets&quot; to assign monthly targets.</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-md3-outline-variant hover:bg-transparent">
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Line Item</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Target</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Actual</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Achievement</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Earned %</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant">Data Source</TableHead>
                            <TableHead className="text-md3-label-large text-md3-on-surface-variant text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {targetsWithActuals.map((target) => (
                            <TableRow key={target.id} className="border-md3-outline-variant">
                              <TableCell className="text-md3-body-medium text-md3-on-surface font-medium">
                                <div>
                                  {target.kra_line_item_name}
                                  <span className="text-md3-body-small text-md3-on-surface-variant ml-2">({target.weightage}%)</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-md3-title-medium text-md3-primary">{target.target_value}</span>
                                <span className="text-md3-body-small text-md3-on-surface-variant ml-1">{target.measurement_unit}</span>
                              </TableCell>
                              <TableCell>
                                {target.has_actual ? (
                                  <span className="text-md3-title-medium text-md3-tertiary">{target.actual_value}</span>
                                ) : (
                                  <span className="text-md3-body-medium text-md3-outline">—</span>
                                )}
                                <span className="text-md3-body-small text-md3-on-surface-variant ml-1">{target.measurement_unit}</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="w-20 h-2 bg-md3-surface-container-highest rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full rounded-full transition-all ${
                                        target.achievement_percent >= 100 ? 'bg-md3-tertiary' :
                                        target.achievement_percent >= 75 ? 'bg-md3-primary' :
                                        target.achievement_percent >= 50 ? 'bg-yellow-500' :
                                        'bg-md3-error'
                                      }`}
                                      style={{ width: `${Math.min(target.achievement_percent, 100)}%` }}
                                    />
                                  </div>
                                  <span className={`text-md3-label-large ${
                                    target.achievement_percent >= 100 ? 'text-md3-tertiary' :
                                    target.achievement_percent >= 75 ? 'text-md3-primary' :
                                    target.achievement_percent >= 50 ? 'text-yellow-600' :
                                    'text-md3-error'
                                  }`}>
                                    {target.achievement_percent}%
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant="outline" 
                                  className={`${
                                    target.earned_weightage > 0 ? 'border-md3-tertiary text-md3-tertiary bg-md3-tertiary-container/30' : 'border-md3-outline text-md3-outline'
                                  }`}
                                >
                                  {target.earned_weightage}%
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className={
                                  target.data_source_type === 'auto' 
                                    ? 'border-md3-tertiary text-md3-tertiary' 
                                    : 'border-md3-outline text-md3-on-surface-variant'
                                }>
                                  {target.data_source_type === 'auto' ? target.actual_data_source || 'Auto' : 'Manual'}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  {/* Manual entry button for manual data source */}
                                  {target.data_source_type !== 'auto' && canEdit && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => openManualActualDialog(target)}
                                      className="h-8 w-8 p-0 text-md3-secondary hover:bg-md3-secondary-container rounded-md3-full"
                                      title="Enter Actual Value"
                                    >
                                      <FileEdit className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {/* Direct Edit button - only for Admin/Management */}
                                  {isAdminOrManagement && canEdit && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => openEditTargetDialog(target)}
                                      className="h-8 w-8 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                      title="Edit Target"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canEdit && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => openReviseDialog(target)}
                                      className="h-8 w-8 p-0 text-md3-tertiary hover:bg-md3-tertiary-container rounded-md3-full"
                                      title="Revise Target"
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {canDelete && (
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => setDeleteDialog({ open: true, type: 'target', item: target })}
                                      className="h-8 w-8 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    
                    {/* Summary Row */}
                    {targetsWithActuals.length > 0 && (
                      <div className="mt-4 p-4 bg-md3-surface-container rounded-md3-lg border border-md3-outline-variant">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-md3-title-medium text-md3-on-surface">Total Earned Weightage</p>
                            <p className="text-md3-body-small text-md3-on-surface-variant">Based on achievement against targets</p>
                          </div>
                          <div className="text-right">
                            <p className="text-md3-headline-medium text-md3-tertiary">
                              {targetsWithActuals.reduce((sum, t) => sum + (t.earned_weightage || 0), 0).toFixed(1)}%
                            </p>
                            <p className="text-md3-body-small text-md3-on-surface-variant">
                              of {targetsWithActuals.reduce((sum, t) => sum + (t.weightage || 0), 0)}% total
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Assignment Dialog */}
      <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              {editingAssignment ? 'Edit Assignment' : 'New KRA Assignment'}
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Assign a KRA type to an employee or intern
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignmentSubmit} className="space-y-4">
            {!editingAssignment && (
              <>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Employee Type *</Label>
                  <Select 
                    value={assignmentForm.employee_type} 
                    onValueChange={(v) => setAssignmentForm({ ...assignmentForm, employee_type: v, employee_id: "" })}
                  >
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Employee *</Label>
                  <Select 
                    value={assignmentForm.employee_id} 
                    onValueChange={(v) => {
                      const emp = employees.find(e => e.id === v);
                      setAssignmentForm({ 
                        ...assignmentForm, 
                        employee_id: v,
                        department_id: emp?.department || ""
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.filter(e => e.employee_type === assignmentForm.employee_type).map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.display_name} - {emp.department}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">KRA Type *</Label>
                  <Select 
                    value={assignmentForm.kra_type_id} 
                    onValueChange={(v) => setAssignmentForm({ ...assignmentForm, kra_type_id: v })}
                  >
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                      <SelectValue placeholder="Select KRA type" />
                    </SelectTrigger>
                    <SelectContent>
                      {kraTypes.map(kra => (
                        <SelectItem key={kra.id} value={kra.id}>
                          {kra.name} ({kra.department_name})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Effective From *</Label>
                  <Input
                    type="date"
                    value={assignmentForm.effective_from}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, effective_from: e.target.value })}
                    required
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                </div>
              </>
            )}
            <Separator />
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Approver Type *</Label>
              <Select 
                value={assignmentForm.approver_type} 
                onValueChange={(v) => setAssignmentForm({ ...assignmentForm, approver_type: v })}
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="mentor">Mentor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Approver *</Label>
              <Select 
                value={assignmentForm.approver_id} 
                onValueChange={(v) => setAssignmentForm({ ...assignmentForm, approver_id: v })}
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue placeholder="Select approver" />
                </SelectTrigger>
                <SelectContent>
                  {approvers.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.display_name} - {a.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setAssignmentDialogOpen(false)}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
              >
                {editingAssignment ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Assignment Dialog (for changing KRA type) */}
      <Dialog open={changeAssignmentDialog.open} onOpenChange={(open) => setChangeAssignmentDialog({ ...changeAssignmentDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Change KRA Assignment
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Change the KRA type for {changeAssignmentDialog.assignment?.employee_name}. 
              The current assignment will end on the day before the new effective date.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleChangeAssignmentSubmit} className="space-y-4">
            <div className="p-3 bg-md3-surface-container rounded-md3-md">
              <p className="text-md3-body-small text-md3-on-surface-variant">Current Assignment</p>
              <p className="text-md3-title-medium text-md3-on-surface">
                {changeAssignmentDialog.assignment?.kra_type_name}
              </p>
              <p className="text-md3-body-small text-md3-on-surface-variant">
                Since {changeAssignmentDialog.assignment?.effective_from}
              </p>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">New KRA Type *</Label>
              <Select 
                value={changeAssignmentForm.new_kra_type_id} 
                onValueChange={(v) => setChangeAssignmentForm({ ...changeAssignmentForm, new_kra_type_id: v })}
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue placeholder="Select new KRA type" />
                </SelectTrigger>
                <SelectContent>
                  {kraTypes.filter(k => k.id !== changeAssignmentDialog.assignment?.kra_type_id).map(k => (
                    <SelectItem key={k.id} value={k.id}>
                      {k.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">New Effective From *</Label>
              <Input
                type="date"
                value={changeAssignmentForm.new_effective_from}
                onChange={(e) => setChangeAssignmentForm({ ...changeAssignmentForm, new_effective_from: e.target.value })}
                required
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
              />
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">New Approver Type *</Label>
              <Select 
                value={changeAssignmentForm.new_approver_type} 
                onValueChange={(v) => setChangeAssignmentForm({ ...changeAssignmentForm, new_approver_type: v })}
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="mentor">Mentor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">New Approver *</Label>
              <Select 
                value={changeAssignmentForm.new_approver_id} 
                onValueChange={(v) => setChangeAssignmentForm({ ...changeAssignmentForm, new_approver_id: v })}
              >
                <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                  <SelectValue placeholder="Select approver" />
                </SelectTrigger>
                <SelectContent>
                  {approvers.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.display_name} - {a.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Change Reason</Label>
              <Textarea
                value={changeAssignmentForm.change_reason}
                onChange={(e) => setChangeAssignmentForm({ ...changeAssignmentForm, change_reason: e.target.value })}
                placeholder="e.g., Role change, Department transfer, Performance review"
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setChangeAssignmentDialog({ open: false, assignment: null })}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
              >
                Change Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set Targets Dialog */}
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Set Targets for {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Enter target values for each KRA line item
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTargetSubmit} className="space-y-4">
            {targetForm.targets.length === 0 ? (
              <div className="text-center py-8">
                <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                <p className="text-md3-body-medium text-md3-on-surface-variant">
                  No KRA line items defined for this KRA type.
                </p>
                <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                  Please add line items in the KRA Settings page first.
                </p>
              </div>
            ) : (
              targetForm.targets.map((target, index) => (
                <div key={target.kra_line_item_id} className="p-3 bg-md3-surface-container rounded-md3-md">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-md3-body-large font-medium text-md3-on-surface">{target.kra_line_item_name}</span>
                      <Badge variant="outline" className="ml-2 text-xs">{target.weightage}%</Badge>
                    </div>
                    <Badge variant="outline" className={
                      target.data_source_type === 'auto' 
                        ? 'border-md3-tertiary text-md3-tertiary' 
                        : 'border-md3-outline text-md3-on-surface-variant'
                    }>
                      {target.data_source_type === 'auto' ? 'Auto' : 'Manual'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={target.target_value}
                        onChange={(e) => updateTargetValue(index, 'target_value', e.target.value)}
                        placeholder={`Target (${target.measurement_unit})`}
                        className="bg-md3-surface border-md3-outline rounded-md3-sm"
                      />
                    </div>
                    <span className="text-md3-body-medium text-md3-on-surface-variant w-16">{target.measurement_unit}</span>
                  </div>
                </div>
              ))
            )}
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setTargetDialogOpen(false)}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
              >
                Save Targets
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Revise Target Dialog */}
      <Dialog open={reviseDialogOpen} onOpenChange={setReviseDialogOpen}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Revise Target
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Create a new target value effective from a specific date
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReviseSubmit} className="space-y-4">
            <div className="p-3 bg-md3-surface-container rounded-md3-md">
              <p className="text-md3-body-small text-md3-on-surface-variant">Current Target</p>
              <p className="text-md3-title-medium text-md3-on-surface">
                {reviseTarget?.kra_line_item_name}: <strong>{reviseTarget?.target_value}</strong> {reviseTarget?.target_unit}
              </p>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">New Target Value *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={reviseForm.new_target_value}
                onChange={(e) => setReviseForm({ ...reviseForm, new_target_value: e.target.value })}
                required
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
              />
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Effective From *</Label>
              <Input
                type="date"
                value={reviseForm.effective_from}
                onChange={(e) => setReviseForm({ ...reviseForm, effective_from: e.target.value })}
                required
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
              />
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Revision Reason *</Label>
              <Input
                value={reviseForm.revision_reason}
                onChange={(e) => setReviseForm({ ...reviseForm, revision_reason: e.target.value })}
                placeholder="e.g., Role change, Mid-month adjustment"
                required
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
              />
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setReviseDialogOpen(false)}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-tertiary hover:bg-md3-tertiary/90 text-md3-on-tertiary rounded-md3-full"
              >
                Revise Target
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Target Dialog (Direct Edit - for Admin/Management) */}
      <Dialog open={editTargetDialogOpen} onOpenChange={setEditTargetDialogOpen}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Edit Target
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Directly update the target value (Admin/Management only)
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditTargetSubmit} className="space-y-4">
            <div className="p-3 bg-md3-surface-container rounded-md3-md">
              <p className="text-md3-body-small text-md3-on-surface-variant">Line Item</p>
              <p className="text-md3-title-medium text-md3-on-surface">
                {editingTarget?.kra_line_item_name}
              </p>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Target Value *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editTargetForm.target_value}
                  onChange={(e) => setEditTargetForm({ ...editTargetForm, target_value: e.target.value })}
                  required
                  className="bg-md3-surface border-md3-outline rounded-md3-sm"
                />
                <span className="text-md3-body-medium text-md3-on-surface-variant">
                  {editingTarget?.target_unit}
                </span>
              </div>
            </div>
            <div className="p-3 bg-md3-tertiary-container/30 rounded-md3-md">
              <p className="text-md3-body-small text-md3-on-surface-variant">
                Note: This will directly update the target value without creating a revision record.
              </p>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditTargetDialogOpen(false)}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
              >
                Update Target
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manual Actual Entry Dialog */}
      <Dialog open={manualActualDialog.open} onOpenChange={(open) => setManualActualDialog({ ...manualActualDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Enter Actual Value
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Record the actual achievement for this KRA line item
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleManualActualSubmit} className="space-y-4">
            <div className="p-3 bg-md3-surface-container rounded-md3-md">
              <p className="text-md3-body-small text-md3-on-surface-variant">Line Item</p>
              <p className="text-md3-title-medium text-md3-on-surface">
                {manualActualDialog.target?.kra_line_item_name}
              </p>
              <div className="flex items-center gap-4 mt-2">
                <div>
                  <p className="text-md3-body-small text-md3-on-surface-variant">Target</p>
                  <p className="text-md3-body-large text-md3-primary">
                    {manualActualDialog.target?.target_value} {manualActualDialog.target?.measurement_unit}
                  </p>
                </div>
                {manualActualDialog.target?.has_actual && (
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Current Actual</p>
                    <p className="text-md3-body-large text-md3-tertiary">
                      {manualActualDialog.target?.actual_value} {manualActualDialog.target?.measurement_unit}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Actual Value *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualActualForm.actual_value}
                  onChange={(e) => setManualActualForm({ ...manualActualForm, actual_value: e.target.value })}
                  required
                  className="bg-md3-surface border-md3-outline rounded-md3-sm"
                />
                <span className="text-md3-body-medium text-md3-on-surface-variant">
                  {manualActualDialog.target?.measurement_unit}
                </span>
              </div>
            </div>
            <div>
              <Label className="text-md3-label-large text-md3-on-surface">Notes (Optional)</Label>
              <Textarea
                value={manualActualForm.notes}
                onChange={(e) => setManualActualForm({ ...manualActualForm, notes: e.target.value })}
                placeholder="Add any relevant notes about this entry..."
                className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setManualActualDialog({ open: false, target: null })}
                className="border-md3-outline text-md3-primary rounded-md3-full"
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-md3-secondary hover:bg-md3-secondary/90 text-md3-on-secondary rounded-md3-full"
              >
                Save Actual
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
          <DialogHeader>
            <DialogTitle className="text-md3-headline-small text-md3-on-surface">
              Delete {deleteDialog.type === 'assignment' ? 'Assignment' : 'Target'}
            </DialogTitle>
            <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
              Are you sure you want to delete this {deleteDialog.type}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, type: null, item: null })}
              className="border-md3-outline text-md3-primary rounded-md3-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={deleteDialog.type === 'assignment' ? handleDeleteAssignment : handleDeleteTarget}
              className="bg-md3-error hover:bg-md3-error/90 text-md3-on-error rounded-md3-full"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
